/**
 *
 * CreateMessage
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

// styles
import './style.scss';

// layout components
import Preloader from 'components/Preloader';
import ErrorBoundary from 'engine/ErrorBoundary';

// antd component
import { ActionSheet, Toast } from 'antd-mobile';
import { Form, Input, Button, message } from 'antd';

// assets
import CustomIcons from 'components/CustomIcons';
import { CloseOutlined, SendOutlined } from '@ant-design/icons';

// Communication components
import { AudioRecorder } from 'components/Audio';
import TCFModal from 'components/TCFModal';
import HiddenField from 'components/Forms/Fields/HiddenField';

// utils
import api, { setAuthorizationHeader } from 'utils/apis/axiosAPI';
import { truncate } from 'utils/helpers/helper.general';
// hooks
import { useRecorder } from 'utils/hooks';
// capacitor hekpers
import { appHaptics } from 'appCapacitor/helpers';

// global user
import {
  useGetLocalUser,
  useGetLocalActiveChild,
} from 'engine/Contexts/User.context';

const CreateMessageMobile = ({
  disabled,
  loading,
  onSend,
  setReply,
  reply,
  messagesListRef,
  dataList,
  // default props
  className,
}) => {
  const childClassNames = classNames('create-message-mobile', className);

  const localUser = useGetLocalUser()();
  const localActiveChild = useGetLocalActiveChild()();

  const [discussionForm] = Form.useForm();
  const textAreaRef = useRef(null);
  const recorder = useRecorder();

  const messageContentValue = Form.useWatch(['content'], discussionForm);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);

  let aggrementTimeout = false;

  useEffect(() => {
    return () => {
      !!aggrementTimeout && clearTimeout(aggrementTimeout);
    }
  }, []);

  useEffect(() => {
    if (textAreaRef.current) {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      if (
        textAreaRef.current.resizableTextArea.textArea.clientHeight <= 120 ||
        textAreaRef.current.resizableTextArea.textArea.scrollHeight <= 150
      ) {
        textAreaRef.current.resizableTextArea.textArea.style.height = '0px';
        const scrollHeight =
          textAreaRef.current.resizableTextArea.textArea.scrollHeight;

        // We then set the height directly, outside of the render loop
        // Trying to set this with state or a ref will product an incorrect value.
        textAreaRef.current.resizableTextArea.textArea.style.height =
          scrollHeight + 2 + 'px';
      }
    }
  }, [textAreaRef.current, messageContentValue, recorder.audio]);

  useEffect(() => {
    if (messagesListRef.current && textAreaRef.current) {
      messagesListRef.current.style.height = `calc(100vh - ${textAreaRef.current
        .resizableTextArea.textArea.clientHeight +
        113 +
        (reply ? 35 : 0)}px)`;
    }
  }, [
    messagesListRef,
    messageContentValue,
    textAreaRef,
    dataList,
    recorder.audio,
    reply,
  ]);

  const handleFormFinish = values => {
    
    if (values?.content || recorder?.audio) {
      if (recorder?.audio) {
        setUploadLoading(true);
        const file = new File([recorder.audio], `files-${uuidv4()}.webm`, {
          type: 'audio/webm',
        });

        const form = new FormData();
        form.append('audio', file);
        
        const sentMsgObject = {
          student: localActiveChild?._id,
          media: {
            type: 'audio',
            url: data.url,
          },
        }

        if (!!values?.visibility) {
          sentMsgObject.visibility = values.visibility;
        }

        api.discussions
          .uploadVoiceMessage(form)
          .then(data => {
            onSend(sentMsgObject);
            setConfirmModalOpened(false);
            setUploadLoading(false);
            recorder.deleteRecording();
          })
          .catch(err => {
            setUploadLoading(false);
            console.error(err);
            discussionForm.resetFields();
          });
      } else {
        const { visibility, ...restVal } = values;
        const sentMsgObject = {
          ...restVal,
          student: localActiveChild?._id,
        }
        if (!!visibility) {
          sentMsgObject.visibility = visibility;
        }
        onSend(sentMsgObject);
        discussionForm.resetFields();
        setConfirmModalOpened(false);
      }
    } else {
      message.error('You have to send either an audio or a message');
      setUploadLoading(false);
    }
  };

  const renderReplay = useMemo(() => {
    return (
      !!reply && (
        <div className="message-reply-mobile">
          <div className="message-reply-mobile--header">
            <CustomIcons
              type="reply-left"
              className="message-reply-mobile--header__icon"
            />
            <div className="message-reply-mobile--header__content">
              <span className="reply-name">
                {reply?.sender?.description?.firstname}{' '}
                {reply?.sender?.description?.lastname}
              </span>
              <div
                className="message-reply-mobile__text"
                dangerouslySetInnerHTML={{
                  __html: reply?.content
                    ? truncate(reply?.content, 50)
                    : 'Voice message',
                }}
              />
            </div>
          </div>
          <CloseOutlined onClick={() => setReply(null)} />
        </div>
      )
    );
  }, [reply, setReply]);

  const onSaveMessage = useCallback(() => { 
    discussionForm.submit();
    appHaptics.hapticsImpactLight();
  }, []);

  // Modal
  const onVisibleForAllHandler = useCallback(() => {
    discussionForm.setFieldValue(['visibility'], 'all');
    onSaveMessage();
  }, []);
  const onVisibleForAdminsHandler = useCallback(() => {
    discussionForm.setFieldValue(['visibility'], 'admin');
    onSaveMessage();
    !loading && setConfirmModalOpened(false);
  }, [loading]);

  const renderAggrementModal = useCallback(() => {
    return (
      <TCFModal
        wrapClassName="create-message-modal"
        open={confirmModalOpened}
        setOpen={setConfirmModalOpened}
        title="Rendre ce message uniquement visible par l’administration scolaire et les enseignants"
        loading={loading}
        confirmLoading={loading}
        onCancel={onVisibleForAllHandler}
        onOk={onVisibleForAdminsHandler}
        cancelName="Non"
        submitName="Oui"
      >
        <></>
      </TCFModal>
    );
  }, [confirmModalOpened, loading]);

  const renderAggrementSheet = useCallback(() => {
    return (
      <ActionSheet
        popupClassName="create-message-sheet"
        extra="Rendre ce message uniquement visible par l’administration scolaire et les enseignants"
        visible={confirmModalOpened}
        safeArea={true}
        actions={[
          {
            text: 'Non',
            key: 'all',
            // description: 'description',
            danger: false,
            bold: true,
            onClick: () => onVisibleForAllHandler(),
          },
          {
            text: 'Oui',
            key: 'admin',
            //  description: 'description',
            danger: false,
            bold: true,
            onClick: () => onVisibleForAdminsHandler(),
          },
        ]}
        onClose={() => setConfirmModalOpened(false)}
      />
    );
  }, [confirmModalOpened, loading]);

  return (
    <div className={childClassNames}>
      <ErrorBoundary>
        {renderReplay}
        <Form
          form={discussionForm}
          name="new_message_form"
          className={classNames(
            `message-form-mobile`,
            disabled && 'disabled',
            (loading || uploadLoading) && 'loading',
          )}
          onFinish={handleFormFinish}
          //disabled={disabled}
        >
          <HiddenField data={{ name: 'visibility' }} index={0} />

          {!recorder.isRecording && !recorder.audio && (
            <Form.Item name="content" noStyle>
              <Input.TextArea
                rows={2}
                placeholder="Votre message"
                className="message-form-mobile--content"
                ref={textAreaRef}
              />
            </Form.Item>
          )}
          {(!messageContentValue ||
            (messageContentValue && messageContentValue.length === 0)) && (
            <AudioRecorder
              loading={loading || uploadLoading}
              recorder={recorder}
            />
          )}
          {(messageContentValue?.length > 0 || recorder.audio) && (
            <Form.Item noStyle>
              <div
                className={classNames(
                  'message-form-mobile__submit',
                  (loading || uploadLoading) && 'loading',
                )}
              >
                {(loading || uploadLoading) && <Preloader size="small" />}
                {!loading && !uploadLoading && (
                  <Button
                    className="message-form-mobile__submit-btn"
                    type="default"
                    htmlType="button"
                    onClick={() => {
                      if (localUser?.role == 'teacher') {
                        if (!!reply && reply?.senderRole == 'admin' || reply?.senderRole == 'teacher') {
                          onSaveMessage();
                        } else {
                          aggrementTimeout = setTimeout(setConfirmModalOpened(true), 300);
                        }
                      } else {
                        aggrementTimeout = setTimeout(setConfirmModalOpened(true), 300);
                      }
                    }}
                  >
                    <SendOutlined />
                  </Button>
                )}
              </div>
            </Form.Item>
          )}

{/*           {renderAggrementModal()} */}
           {renderAggrementSheet()}
        </Form>
      </ErrorBoundary>
    </div>
  );
};

CreateMessageMobile.defaultProps = {
  loading: false,
  disabled: false,
};
CreateMessageMobile.propTypes = {};

export default CreateMessageMobile;
